import { FC, useCallback, useRef } from 'react'
import { colors, styled, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import ViewListIcon from '@mui/icons-material/ViewList'
import GridViewIcon from '@mui/icons-material/GridView'
import { DataSurfaceViewMode } from './types'

export interface IButtonSwitchProps {
  value?: DataSurfaceViewMode
  onChange?: (value: DataSurfaceViewMode) => void
  size?: 'sm' | 'md'
  className?: string
  onlyIcon?: boolean
  reverse?: boolean
}

const sizeMap = { sm: 'small', md: 'medium' } as const

export const mapIcons: Record<DataSurfaceViewMode, JSX.Element> = {
  [DataSurfaceViewMode.list]: <ViewListIcon fontSize='small' />,
  [DataSurfaceViewMode.grid]: <GridViewIcon fontSize='small' />
}

const viewModes = [
  { value: DataSurfaceViewMode.list, icon: mapIcons[DataSurfaceViewMode.list], label: 'List View' },
  { value: DataSurfaceViewMode.grid, icon: mapIcons[DataSurfaceViewMode.grid], label: 'Grid View' }
]

export const ButtonSwitch: FC<IButtonSwitchProps> = (props) => {
  const { value, onChange, size = 'md', className, onlyIcon = false, reverse = false } = props
  const muiSize = sizeMap[size] || 'small'

  const changeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const handleChange = useCallback(
    (newValue: DataSurfaceViewMode | null) => {
      if (changeTimeoutRef.current) {
        clearTimeout(changeTimeoutRef.current)
      }
      changeTimeoutRef.current = setTimeout(() => {
        if (newValue !== null && newValue !== value) {
          onChange?.(newValue)
        }
      }, 250)
    },
    [value, onChange]
  )

  const displayModes = reverse ? [...viewModes].reverse() : viewModes

  return (
    <StyledToggleButtonGroup className={className} size={muiSize} value={value} exclusive onChange={(_, v) => handleChange(v)} aria-label='view mode'>
      {displayModes.map((mode) => (
        <Tooltip key={mode.value} title={mode.label} arrow placement='top'>
          <ToggleButton
            value={mode.value}
            aria-label={mode.label}
            size={muiSize}
            sx={{
              p: onlyIcon ? '5px' : undefined,
              minWidth: onlyIcon ? (muiSize === 'small' ? 36 : 44) : undefined
            }}
          >
            {onlyIcon ? mode.icon : mode.label}
          </ToggleButton>
        </Tooltip>
      ))}
    </StyledToggleButtonGroup>
  )
}

export default ButtonSwitch

const StyledToggleButtonGroup = styled(ToggleButtonGroup)({
  flex: '0 0 auto',
  '.MuiToggleButtonGroup-grouped': {
    '&.Mui-selected': {
      color: colors.common.white,
      backgroundColor: `var(--color-orange, ${colors.blue[600]})`,
      borderColor: `var(--color-orange, ${colors.blue[600]})`,
      '&.MuiToggleButtonGroup-lastButton': {
        borderColor: `var(--color-orange, ${colors.blue[600]})`
      },
      '&:hover': {
        backgroundColor: `var(--color-orange, ${colors.blue[600]})`
      }
    }
  }
})
